import type { ReactNode } from "react";

/**
 * One titled block of a customer page (ADR-0088): a bold heading with its action on the right,
 * then the content 12px below. Pages stack these with `flex flex-col gap-8`, never `space-y`, so a
 * child's own margin can never collapse the gap between sections.
 */
export function PageSection({
  title,
  id,
  level = "h2",
  action,
  description,
  children,
}: {
  title: string;
  /** The heading id, for pages that label the section by it; otherwise the title labels it. */
  id?: string;
  level?: "h2" | "h3";
  action?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  const Heading = level;
  return (
    <section
      {...(id ? { "aria-labelledby": id } : { "aria-label": title })}
      className="flex flex-col gap-3"
    >
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Heading id={id} className={level === "h2" ? "text-xl font-bold" : "text-lg font-bold"}>
            {title}
          </Heading>
          {description ? (
            <p className="mt-0.5 max-w-3xl text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** What a section says when it has nothing yet: a quiet card, never a dashed box. */
export function EmptyCard({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl bg-card px-6 py-5 text-muted-foreground shadow-card">{children}</p>
  );
}
